<script setup lang="ts">
import WebsitesService from '#layers/cms/app/pages/cms/management/websites/_runtime/services/WebsitesService';
import type { PageListItemModel } from '#layers/cms/app/pages/cms/management/pages/_runtime/services/PagesService';
import PagesService from '#layers/cms/app/pages/cms/management/pages/_runtime/services/PagesService';
import PreviewService from '#layers/cms/app/runtime/services/PreviewService';
import { useWebsitesStore } from '#layers/cms/app/pages/cms/management/websites/_runtime/stores/websites';

const emit = defineEmits<{
    (e: 'update:modelValue', value: number): void;
    (e: 'preview', url: string): void;
}>();

const props = withDefaults(defineProps<{
    modelValue?: number;
    module?: string;
    disabled?: boolean;
    preview?: boolean;
    website?: boolean;
    label?: string;
    noPreview?: boolean;
    websiteClass?: string;
    websiteId?: number;
    locale?: string;
}>(), {
    modelValue: null,
    module: '',
    disabled: false,
    preview: true,
    website: true,
    label: '[[[Publikuj na stronie]]]',
    noPreview: false,
    websiteClass: '',
    websiteId: null,
    locale: null
});

const layoutContext = inject<ComputedRef<{ websiteId: number; locale: string } | null>>('layoutPageSelectorContext', null);

const store = useWebsitesStore();
const { $log } = useLogging();
const { $alert } = useAlerts();

const pages = ref<PageListItemModel[]>([]);
const pagesReady = ref(false);
const current = computed<CurrentWebsite>(() =>
{
    return store.current;
});

function normalizePageId(value: number | string | null | undefined): number | null
{
    if (value == null || value === '')
        return null;

    const parsed = Number(value);

    return Number.isNaN(parsed) ? null : parsed;
}

const pageId = computed({
    get(): number | null
    {
        return normalizePageId(props.modelValue);
    },
    set(value: number | null)
    {
        triggerModel(value);
    }
});

const treePageId = computed({
    get(): number | null
    {
        const id = pageId.value;

        if (id == null || !pagesReady.value)
            return null;

        if (!pages.value.some(p => p.id == id))
            return null;

        return id;
    },
    set(value: number | null)
    {
        pageId.value = value;
    }
});

const page = computed<any>(() =>
{
    return pages.value.find(p => p.id == pageId.value);
});

function triggerModel(value: number | null): void
{
    if (value == null)
    {
        emit('update:modelValue', null);

        return;
    }

    if (typeof props.modelValue === 'string')
        emit('update:modelValue', value.toString() as any);
    else
        emit('update:modelValue', value);
}

function effectiveWebsiteId(): number
{
    if (props.website)
        return current.value.website ?? props.websiteId;

    return layoutContext?.value?.websiteId ?? props.websiteId ?? current.value.website;
}

function effectiveLocale(): string
{
    if (props.website)
        return current.value.locale ?? props.locale;

    return layoutContext?.value?.locale ?? props.locale ?? current.value.locale;
}

async function loadPages(): Promise<void>
{
    const websiteId = effectiveWebsiteId();
    const locale = effectiveLocale();

    pagesReady.value = false;

    if (!websiteId || !locale)
        return;

    try
    {
        const response = await PagesService.getPagesByModule(websiteId, locale, props.module);

        pages.value = response.map((p) =>
        {
            p.disabled = !p.module || props.module !== p.module;

            return p;
        });
    }
    catch (ex)
    {
        pages.value = [];
        $log.debug(ex);
    }
    finally
    {
        pagesReady.value = true;
    }
}

function hasContextValue(value: unknown): boolean
{
    return value != null && value !== '' && value !== 0;
}

async function handleWebsiteChange(): Promise<void>
{
    pageId.value = null;
    await loadPages();
}

async function handleLocaleChange(): Promise<void>
{
    pageId.value = null;
    await loadPages();
}

async function onPreview(): Promise<string>
{
    try
    {
        const token = await PreviewService.getPreviewToken();
        const website = (await WebsitesService.fetch(effectiveWebsiteId())).result;
        const scheme = website.isSecured ? 'https://' : 'http://';
        const url = `${scheme}${website.baseUrl}/preview/${effectiveLocale()}/${pageId.value}/{0}?access_token=${token}`;

        emit('preview', url);

        return url;
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
}

watch(() => props.website
    ? [current.value.website, current.value.locale]
    : [props.websiteId, props.locale, layoutContext?.value?.websiteId, layoutContext?.value?.locale], async (values, oldValues) =>
{
    const changed = values.some((value, index) => value !== oldValues?.[index]);

    if (changed && oldValues?.some(value => hasContextValue(value)))
    {
        pageId.value = null;
    }

    await loadPages();
});

onBeforeMount(async (): Promise<void> =>
{
    if (effectiveWebsiteId() && effectiveLocale())
    {
        await loadPages();

        return;
    }

    await wait(() => current.value.website);
    await loadPages();
});

defineExpose({
    page,
    onPreview
});
</script>

<template>
    <IdeoFormGroup :label="$t(label)" style="container-type: inline-size;">
        <div class="page-selector d-flex gap-2">
            <EditoWebsiteSelector v-if="website" :disabled="disabled" :class="websiteClass" @change-website="handleWebsiteChange" @change-locale="handleLocaleChange" />
            <div class="flex-fill">
                <IdeoFormTreeSelect
                    v-if="pagesReady"
                    v-model="treePageId"
                    :disabled="disabled"
                    :options="pages"
                    transform
                    :empty-option="$t('[[[Wybierz stronę]]]')"
                />
            </div>
            <div v-if="!noPreview">
                <IdeoButton v-if="preview" variant="info" :title="$t('[[[Zobacz przed opublikowaniem]]]')" :disabled="!pageId || disabled" @click="onPreview()">
                    <i class="fas fa-fw fa-search" />
                </IdeoButton>
            </div>
        </div>
    </IdeoFormGroup>
</template>

<style>
@container (max-width: 400px) {
    .page-selector {
        flex-wrap: wrap;
    }
}
</style>
